import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Node, Edge, OnNodesChange, OnEdgesChange } from '@xyflow/react';
import { applyNodeChanges, applyEdgeChanges } from '@xyflow/react';

interface CanvasState {
  nodes: Node[];
  edges: Edge[];
  selectedNodeIds: string[];
  activeProjectId: string | null;
  isLoading: boolean;

  setNodes: (nodes: Node[]) => void;
  setEdges: (edges: Edge[]) => void;
  hydrate: (nodes: Node[], edges: Edge[]) => void;
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  addNode: (node: Node) => void;
  updateNode: (id: string, data: Partial<Node['data']>) => void;
  removeNode: (id: string) => void;
  addEdge: (edge: Edge) => void;
  removeEdge: (id: string) => void;
  setSelectedNodeIds: (ids: string[]) => void;
  setActiveProjectId: (id: string | null) => void;
  setIsLoading: (loading: boolean) => void;
  reset: () => void;
}

const initialState = {
  nodes: [] as Node[],
  edges: [] as Edge[],
  selectedNodeIds: [] as string[],
  activeProjectId: null as string | null,
  isLoading: false,
};

export const useCanvasStore = create<CanvasState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setNodes: (nodes) => set({ nodes }),
      setEdges: (edges) => set({ edges }),

      // Load a project's server graph into the store (initial canvas load and
      // the rollback refetch after a failed persist).
      hydrate: (nodes, edges) => set({ nodes, edges }),

      onNodesChange: (changes) => {
        set({ nodes: applyNodeChanges(changes, get().nodes) });
      },

      onEdgesChange: (changes) => {
        set({ edges: applyEdgeChanges(changes, get().edges) });
      },

      addNode: (node) => set((state) => ({ nodes: [...state.nodes, node] })),

      updateNode: (id, data) =>
        set((state) => ({
          nodes: state.nodes.map((n) =>
            n.id === id ? { ...n, data: { ...n.data, ...data } } : n
          ),
        })),

      removeNode: (id) =>
        set((state) => ({
          nodes: state.nodes.filter((n) => n.id !== id),
          edges: state.edges.filter((e) => e.source !== id && e.target !== id),
        })),

      addEdge: (edge) => set((state) => ({ edges: [...state.edges, edge] })),

      removeEdge: (id) =>
        set((state) => ({
          edges: state.edges.filter((e) => e.id !== id),
        })),

      setSelectedNodeIds: (ids) => set({ selectedNodeIds: ids }),
      setActiveProjectId: (id) => set({ activeProjectId: id }),
      setIsLoading: (loading) => set({ isLoading: loading }),
      reset: () => set(initialState),
    }),
    { name: 'canvas-store' }
  )
);
